import type {OnCompileContext, ObjectPath, TemplateNodeDefinition} from "#template/template-node/types"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {z} from "zod/v4-mini"
import {TemplateNode} from "#template/template-node/template-node"
import {materialAssignmentLikeMarker} from "#template/role-markers"
import type {MaterialAssignmentLikeNode} from "#template/role-markers"
import type {MaterialLikeNode, MaterialSide, NumberLike} from "#template/nodes/types"
import {materialSide} from "#template/nodes/validation"
import {materialLike} from "#template/nodes/material/fundamental"
import {numberLike} from "#template/nodes/number/fundamental"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {BuilderInlet, BuilderOutlet} from "#template/runtime-graph/graph-builder"
import type {IMaterialData, MeshRenderSettings} from "@cm/material-nodes"
import {transformOffsetUvs} from "@cm/material-nodes"
import type {UvOffset} from "#template/interfaces/scene-manager"
import type {SceneNodes} from "#template/interfaces/scene-object"
import {GetMaterialData} from "#template/runtime-graph/nodes/get-material-data"
import {GetMaterialMetaData} from "#template/runtime-graph/nodes/get-material-meta-data"
import {keyForMeshMaterialData} from "#template/interfaces/material-data"
import type {TypedImageData} from "@cm/utils/typed-image-data"
import {isTypedImageData} from "@cm/utils/typed-image-data"
import type {DataObject} from "@cm/utils"

export type MaterialAssignmentParameters = {
    node: MaterialLikeNode | null
    side: MaterialSide
    horizontalOffset?: NumberLike
    verticalOffset?: NumberLike
    rotation?: NumberLike
    alphaHashing: boolean
}

export type MaterialAssignmentConstructorParameters = Omit<MaterialAssignmentParameters, "alphaHashing"> & {
    alphaHashing?: boolean
}

const materialAssignmentDefinition: TemplateNodeDefinition<MaterialAssignmentParameters, MaterialAssignmentConstructorParameters> = {
    nodeClass: "MaterialAssignment",
    validation: {
        parameters: z.object({
            node: z.nullable(materialLike),
            horizontalOffset: z.optional(numberLike),
            verticalOffset: z.optional(numberLike),
            rotation: z.optional(numberLike),
            side: materialSide,
            alphaHashing: z._default(z.boolean(), false),
        }),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class MaterialAssignment
    extends TemplateNode<MaterialAssignmentParameters, MaterialAssignmentConstructorParameters>
    implements MaterialAssignmentLikeNode
{
    get [materialAssignmentLikeMarker]() {
        return true as const
    }

    public override get definition() {
        return materialAssignmentDefinition
    }

    setupMaterialAssignment(
        scope: GraphBuilderScope,
        context: OnCompileContext,
        parentScope: GraphBuilderScope,
        path: BuilderInlet<ObjectPath>,
        meshRenderSettings: BuilderInlet<MeshRenderSettings> | undefined,
        overriddenMaterial?: MaterialLikeNode | null,
    ) {
        const {evaluator, sceneProperties} = context
        const {templateContext} = evaluator
        const {sceneManager, lodType, computeDataObjectReferences, logger} = templateContext
        const {node: material, horizontalOffset, verticalOffset, rotation, side, alphaHashing} = this.parameters
        const evaluatedMaterial = overriddenMaterial ?? material
        let [materialGraph, matGraphInvalid] = scope.branch(evaluator.evaluate(evaluatedMaterial))

        const nullToZero = (number: BuilderInlet<number | null>, uniqueId: string) => scope.pureLambda(number, (number) => number ?? 0, uniqueId)

        if (horizontalOffset || verticalOffset || rotation) {
            const offset = scope.struct<UvOffset>("UvOffset", {
                horizontal: nullToZero(evaluator.evaluate(horizontalOffset ?? 0), `horizontal`),
                vertical: nullToZero(evaluator.evaluate(verticalOffset ?? 0), `vertical`),
                rotation: nullToZero(evaluator.evaluate(rotation ?? 0), `rotation`),
            })

            materialGraph = scope.pureLambda(
                scope.tuple(materialGraph, offset),
                ([materialGraph, offset]) => transformOffsetUvs(materialGraph, offset),
                `transformOffsetUVs`,
            )
        }

        const textureResolution = sceneProperties?.parameters.textureResolution ?? "2000px"
        const sceneTextureFiltering = sceneProperties?.parameters.textureFiltering
        const textureFiltering = sceneTextureFiltering === true ? "linear" : "nearest"
        const textureStreamingMode = sceneProperties?.parameters.textureStreamingMode

        const materialData = scope.node(GetMaterialData, {
            materialGraph,
            side,
            realtimeSettings: scope.struct<IMaterialData["realtimeSettings"]>("RealtimeSettings", {
                disable: overriddenMaterial === undefined && !(sceneProperties?.parameters.enableRealtimeMaterials ?? true),
                textureResolution,
                textureFiltering,
                textureStreamingMode,
                alphaHashing,
            }),
        }).output

        const materialKey = scope.pureLambda(
            scope.tuple(materialData, meshRenderSettings),
            ([materialData, meshRenderSettings]) => keyForMeshMaterialData(materialData, meshRenderSettings),
            `key`,
        )

        const preloadMaterialId = scope.pureLambda(materialKey, (materialKey) => `global:PreloadMaterial@${materialKey}`, `preloadMatKey`)

        const preloadMaterial = scope.phi(
            scope.struct<SceneNodes.PreloadMaterial>("PreloadMaterial", {
                type: "PreloadMaterial",
                id: preloadMaterialId,
                materialData,
                meshRenderSettings,
                path: [],
            }),
            matGraphInvalid,
        )

        const preDisplayList: BuilderOutlet<SceneNodes.SceneNode | SceneNodes.SceneNode[] | null>[] = [preloadMaterial]

        if (computeDataObjectReferences) {
            const materialMetaData = scope.node(GetMaterialMetaData, {
                sceneManager,
                textureResolution: lodType === "pathTraced" ? "original" : textureResolution,
                lodType,
                materialGraph,
                logger,
            }).materialMetaData
            const referencedDataObjects = scope.phi(
                scope.pureLambda(
                    scope.tuple(materialMetaData, preloadMaterialId, path, parentScope),
                    ([materialMetaData, preloadMaterialId, path, parentScope]) => {
                        const result: SceneNodes.DataObjectReference[] = []

                        const idSuffix = new Map<string, number>()

                        const addDataObject = (dataObject: DataObject | TypedImageData, slots: Set<string>, final: boolean) => {
                            if (isTypedImageData(dataObject)) return

                            const id = `${preloadMaterialId}-${dataObject.legacyId}`
                            const suffix = idSuffix.get(id)
                            const finalId = suffix === undefined ? id : `${id}-${suffix}`
                            idSuffix.set(id, (suffix ?? 0) + 1)

                            const dataObjectReference: SceneNodes.DataObjectReference = {
                                type: "DataObjectReference",
                                id: `${parentScope.getPrefix()}:DataObjectReference@${finalId}`,
                                path,
                                dataObject,
                                origin: {
                                    type: "materialAssignment",
                                    nodeId: finalId,
                                    slots,
                                    final,
                                },
                            }

                            result.push(dataObjectReference)
                        }

                        for (const materialMetaDataItem of materialMetaData) {
                            const {resolvedData, slots} = materialMetaDataItem
                            if (resolvedData.lowRes) {
                                addDataObject(resolvedData.lowRes, slots, false)
                            }
                            addDataObject(resolvedData.primary, slots, true)
                        }

                        return result
                    },
                    `referencedDataObjects`,
                ),
                matGraphInvalid,
            )

            preDisplayList.push(referencedDataObjects)
        }

        return {
            materialData: scope.phi(materialData, matGraphInvalid),
            preDisplayList: scope.filterInvalid(scope.sparseMerge<SceneNodes.SceneNode | null>(preDisplayList, "preDisplayList")),
        }
    }
}
